/**
 * Bundle 130 amendment - dedicated modal bodies for the two
 * orphan/replay incident flows on /admin/incidents.
 *
 * Kills the bare `response_id` text-input pattern: replaces it
 * with an event dropdown -> auto-loaded orphan/response table ->
 * dry-run -> guarded Apply.
 *
 * Backend endpoints used:
 *  - GET  /admin/orphan-picker/events
 *  - GET  /admin/orphan-picker/events/{eid}/orphans
 *  - GET  /admin/orphan-picker/events/{eid}/responses
 *  - POST /admin/incident/replay-missed-hooks-for-event
 *  - POST /admin/incident/rescan-connection-intent-v2
 *
 * Hygiene: no em/en dashes, no eslint-disable, no photo/image alt.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Beaker, Play } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

// Shared: event dropdown populated from the orphan-picker endpoint.
// Passes `include_test=true` so QA-lineage events (whose only
// submitted rows live in `test_survey_responses`) still surface.
// Renders an `Orphans: N` badge next to each option so the admin
// can see at a glance which events actually need attention. Zero-
// orphan events STILL appear so an admin who just cleaned an event
// can re-pick it and verify nothing new is stuck.
const EventPicker = ({ value, onChange, testId }) => {
  const [events, setEvents] = useState([]);
  useEffect(() => {
    api.get('/admin/orphan-picker/events?include_test=true')
      .then(({ data }) => { setEvents(data.events || []); })
      .catch(() => setEvents([]));
  }, []);
  return (
    <Select value={value || ''} onValueChange={onChange}>
      <SelectTrigger data-testid={testId || 'orphan-event-picker'}>
        <SelectValue placeholder="Choose an event" />
      </SelectTrigger>
      <SelectContent>
        {events.length === 0 ? (
          <SelectItem value="_none" disabled>
            No events with submitted responses.
          </SelectItem>
        ) : events.map((e) => {
          const n = Number(e.orphan_count || 0);
          const badgeClass = n > 0
            ? 'bg-amber-50 text-amber-800 border-amber-200'
            : 'bg-neutral-100 text-neutral-600 border-neutral-200';
          return (
            <SelectItem
              key={e.id}
              value={e.id}
              data-testid={`orphan-event-opt-${e.id}`}
            >
              <span className="inline-flex items-center gap-2">
                <span>
                  {e.title || e.id}
                  {' * '}
                  {(e.event_date || e.start_datetime || '').slice(0, 10)}
                </span>
                <span
                  className={
                    'text-[10px] rounded border px-1.5 py-0.5 '
                    + badgeClass
                  }
                  data-testid={`orphan-event-badge-${e.id}`}
                >
                  Orphans: {n}
                </span>
              </span>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
};

// -------------------------------------------------------------------
// Bulk replay for an event: pick event -> orphan table -> dry-run
// -> guarded Apply. Server dedupes on
// (response_id, target, when_where); repeated Apply is safe.
// -------------------------------------------------------------------
export const Bundle130BulkReplayBody = () => {
  const [eventId, setEventId] = useState('');
  const [orphans, setOrphans] = useState([]);
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);
  const [dryRun, setDryRun] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [confirmApply, setConfirmApply] = useState(false);

  useEffect(() => {
    if (!eventId) { setOrphans([]); setSelected({}); return; }
    setDryRun(null); setApplyResult(null);
    api.get(`/admin/orphan-picker/events/${eventId}/orphans?include_test=true`)
      .then(({ data }) => {
        const rows = data.orphans || [];
        setOrphans(rows);
        // Default to all selected.
        const nextSel = {};
        rows.forEach((r) => { nextSel[r.response_id] = true; });
        setSelected(nextSel);
      })
      .catch((e) => {
        toast.error(e?.response?.data?.detail
          || 'Could not load orphans.');
        setOrphans([]);
      });
  }, [eventId]);

  const selectedIds = useMemo(
    () => orphans.filter((r) => selected[r.response_id])
      .map((r) => r.response_id),
    [orphans, selected],
  );

  const toggle = (rid) => setSelected(
    (s) => ({ ...s, [rid]: !s[rid] }),
  );
  const toggleAll = (v) => {
    const nextSel = {};
    orphans.forEach((r) => { nextSel[r.response_id] = v; });
    setSelected(nextSel);
  };

  const runDryRun = async () => {
    if (!eventId) { toast.error('Pick an event.'); return; }
    setBusy(true); setApplyResult(null); setConfirmApply(false);
    try {
      const { data } = await api.post(
        '/admin/incident/replay-missed-hooks-for-event',
        { event_id: eventId, dry_run: true, include_test: true },
      );
      setDryRun(data);
      toast.success('Dry-run complete.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Dry-run failed.');
    } finally { setBusy(false); }
  };

  const runApply = async () => {
    if (!eventId) { toast.error('Pick an event.'); return; }
    setBusy(true);
    try {
      const { data } = await api.post(
        '/admin/incident/replay-missed-hooks-for-event',
        { event_id: eventId, dry_run: false, include_test: true },
      );
      setApplyResult(data);
      setConfirmApply(false);
      toast.success(
        `Applied. Inserted ${data?.totals?.inserted || 0}, ` +
        `already existed ${data?.totals?.already_exists || 0}.`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apply failed.');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3"
          data-testid="bundle130-bulk-replay-body">
      <div className="space-y-1">
        <label className="text-xs uppercase tracking-widest text-neutral-500">
          Event *
        </label>
        <EventPicker
          value={eventId}
          onChange={setEventId}
          testId="bundle130-bulk-event-picker"
        />
      </div>

      {eventId ? (
        <div className="rounded border border-neutral-200 bg-white">
          <div className="p-2 flex items-center justify-between border-b border-neutral-100">
            <div className="text-xs text-primary-900">
              Orphaned responses:
              <span className="font-semibold ml-1"
                    data-testid="bundle130-orphan-count">
                {orphans.length}
              </span>
              <span className="text-neutral-500 ml-2">
                (submitted, has confirmed missed_connections,
                zero introductions rows)
              </span>
            </div>
            {orphans.length > 0 && (
              <label className="text-[11px] text-neutral-600 flex items-center gap-1.5">
                <Checkbox
                  checked={selectedIds.length === orphans.length}
                  onCheckedChange={(v) => toggleAll(!!v)}
                  data-testid="bundle130-select-all"
                />
                Select all
              </label>
            )}
          </div>
          <div className="max-h-64 overflow-auto">
            <table className="w-full text-xs"
                    data-testid="bundle130-orphan-table">
              <thead className="bg-neutral-50 text-neutral-500 text-[10px] uppercase">
                <tr>
                  <th className="text-left p-2 w-8"></th>
                  <th className="text-left p-2">Reporter</th>
                  <th className="text-left p-2">Submitted</th>
                  <th className="text-left p-2">Confirmed</th>
                  <th className="text-left p-2">Intros</th>
                </tr>
              </thead>
              <tbody>
                {orphans.length === 0 ? (
                  <tr>
                    <td colSpan={5}
                        className="p-4 text-center text-neutral-500"
                        data-testid="bundle130-orphan-empty">
                      No orphaned responses for this event.
                    </td>
                  </tr>
                ) : orphans.map((r) => (
                  <tr key={r.response_id}
                        className="border-t border-neutral-100"
                        data-testid={`bundle130-orphan-row-${r.response_id}`}>
                    <td className="p-2">
                      <Checkbox
                        checked={!!selected[r.response_id]}
                        onCheckedChange={() => toggle(r.response_id)}
                        data-testid={`bundle130-orphan-check-${r.response_id}`}
                      />
                    </td>
                    <td className="p-2 text-primary-900">
                      {(r.reporter_first_name || '') + ' '
                        + (r.reporter_last_name || '') || '(no name)'}
                      <div className="text-[10px] text-neutral-500">
                        {r.reporter_email || '-'}
                      </div>
                    </td>
                    <td className="p-2 text-neutral-500">
                      {r.submitted_at
                        ? new Date(r.submitted_at).toLocaleString()
                        : '-'}
                    </td>
                    <td className="p-2">
                      {r.confirmed_missed_count}
                    </td>
                    <td className="p-2">
                      <Badge className="bg-amber-50 text-amber-800 border-amber-200 border text-[10px]">
                        {r.intro_rows_count}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="text-xs text-neutral-500">
          Pick an event above to load its orphaned responses.
        </div>
      )}

      {dryRun && (
        <div className="rounded border border-primary-100 bg-primary-50/40 p-3"
              data-testid="bundle130-bulk-dry-run">
          <div className="text-[10px] uppercase tracking-widest text-primary-800 mb-1">
            Dry-run result
          </div>
          <div className="text-xs text-primary-900">
            Responses touched: {dryRun?.per_response?.length || 0}
            {' * '}
            Would insert (max):{' '}
            {(dryRun?.per_response || []).reduce(
              (a, x) => a + (x.would_insert_max || 0), 0,
            )}
          </div>
        </div>
      )}

      {applyResult && (
        <div className="rounded border border-emerald-200 bg-emerald-50/60 p-3"
              data-testid="bundle130-bulk-apply-result">
          <div className="text-[10px] uppercase tracking-widest text-emerald-800 mb-1">
            Apply result
          </div>
          <div className="text-xs text-primary-900">
            Inserted:{' '}
            <span data-testid="bundle130-bulk-inserted">
              {applyResult?.totals?.inserted || 0}
            </span>
            {' * '}
            Already existed:{' '}
            <span data-testid="bundle130-bulk-already">
              {applyResult?.totals?.already_exists || 0}
            </span>
            {' * '}
            Responses touched:{' '}
            {applyResult?.totals?.responses_touched || 0}
          </div>
        </div>
      )}

      <div className="flex gap-2 justify-end pt-1">
        <Button
          variant="outline"
          disabled={busy || !eventId || orphans.length === 0}
          onClick={runDryRun}
          data-testid="bundle130-bulk-dry-run-btn"
        >
          <Beaker className="h-4 w-4 mr-1.5" /> Dry-run
        </Button>
        {!confirmApply ? (
          <Button
            disabled={busy || !eventId || !dryRun}
            onClick={() => setConfirmApply(true)}
            className="bg-primary-800 text-white hover:bg-primary-900"
            data-testid="bundle130-bulk-apply-btn"
          >
            <Play className="h-4 w-4 mr-1.5" /> Apply
          </Button>
        ) : (
          <div className="flex items-center gap-2 rounded border border-red-200 bg-red-50 px-2 py-1"
                data-testid="bundle130-bulk-confirm-inline">
            <span className="text-[11px] text-red-800">
              Confirm apply?
            </span>
            <Button size="sm" variant="outline"
                    onClick={() => setConfirmApply(false)}
                    data-testid="bundle130-bulk-confirm-cancel">
              No
            </Button>
            <Button size="sm"
                    onClick={runApply}
                    className="bg-red-600 text-white hover:bg-red-700"
                    data-testid="bundle130-bulk-confirm-yes">
              Yes, apply
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// -------------------------------------------------------------------
// Rescan v2 for a single response: event dropdown -> response
// picker -> dry-run -> guarded Apply.
// -------------------------------------------------------------------
export const Bundle130RescanV2Body = () => {
  const [eventId, setEventId] = useState('');
  const [responses, setResponses] = useState([]);
  const [responseId, setResponseId] = useState('');
  const [busy, setBusy] = useState(false);
  const [dryRun, setDryRun] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [confirmApply, setConfirmApply] = useState(false);

  useEffect(() => {
    if (!eventId) { setResponses([]); setResponseId(''); return; }
    setDryRun(null); setApplyResult(null); setResponseId('');
    api.get(`/admin/orphan-picker/events/${eventId}/responses?include_test=true`)
      .then(({ data }) => setResponses(data.responses || []))
      .catch((e) => {
        toast.error(e?.response?.data?.detail
          || 'Could not load responses.');
        setResponses([]);
      });
  }, [eventId]);

  const runDryRun = async () => {
    if (!responseId) { toast.error('Pick a response.'); return; }
    setBusy(true); setApplyResult(null); setConfirmApply(false);
    try {
      const { data } = await api.post(
        '/admin/incident/rescan-connection-intent-v2',
        { response_id: responseId, dry_run: true },
      );
      setDryRun(data);
      toast.success('Dry-run complete.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Dry-run failed.');
    } finally { setBusy(false); }
  };

  const runApply = async () => {
    if (!responseId) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        '/admin/incident/rescan-connection-intent-v2',
        { response_id: responseId, dry_run: false },
      );
      setApplyResult(data);
      setConfirmApply(false);
      const created = data?.introductions?.inserted || 0;
      const already = data?.introductions?.already_exists || 0;
      toast.success(
        `Applied. Created ${created}, already existed ${already}.`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apply failed.');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3"
          data-testid="bundle130-rescan-body">
      <div className="space-y-1">
        <label className="text-xs uppercase tracking-widest text-neutral-500">
          Event *
        </label>
        <EventPicker
          value={eventId}
          onChange={setEventId}
          testId="bundle130-rescan-event-picker"
        />
      </div>

      {eventId && (
        <div className="space-y-1">
          <label className="text-xs uppercase tracking-widest text-neutral-500">
            Response *
          </label>
          <Select value={responseId} onValueChange={setResponseId}>
            <SelectTrigger data-testid="bundle130-rescan-response-picker">
              <SelectValue placeholder="Choose a submitted response" />
            </SelectTrigger>
            <SelectContent>
              {responses.length === 0 ? (
                <SelectItem value="_none" disabled>
                  No submitted responses on this event.
                </SelectItem>
              ) : responses.map((r) => (
                <SelectItem key={r.response_id} value={r.response_id}
                              data-testid={`bundle130-rescan-response-opt-${r.response_id}`}>
                  {(r.reporter_first_name || '')
                    + ' ' + (r.reporter_last_name || '')}
                  {' * '}
                  Intros: {r.intro_rows_count}
                  {' / '}
                  Confirmed: {r.confirmed_missed_count}
                  {r.is_orphaned ? ' (orphan)' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {dryRun && (
        <div className="rounded border border-primary-100 bg-primary-50/40 p-3"
              data-testid="bundle130-rescan-dry-run">
          <div className="text-[10px] uppercase tracking-widest text-primary-800 mb-1">
            Dry-run result
          </div>
          <div className="text-xs text-primary-900">
            Response: <span className="font-mono">{dryRun.response_id}</span>
            {' * '}
            Would insert (max):{' '}
            <span data-testid="bundle130-rescan-would-insert">
              {dryRun.would_insert_introductions_max}
            </span>
          </div>
        </div>
      )}

      {applyResult && (
        <div className="rounded border border-emerald-200 bg-emerald-50/60 p-3"
              data-testid="bundle130-rescan-apply-result">
          <div className="text-[10px] uppercase tracking-widest text-emerald-800 mb-1">
            Apply result
          </div>
          <div className="text-xs text-primary-900">
            Created:{' '}
            <span data-testid="bundle130-rescan-created">
              {applyResult?.introductions?.inserted || 0}
            </span>
            {' * '}
            Already existed:{' '}
            <span data-testid="bundle130-rescan-already">
              {applyResult?.introductions?.already_exists || 0}
            </span>
          </div>
        </div>
      )}

      <div className="flex gap-2 justify-end pt-1">
        <Button
          variant="outline"
          disabled={busy || !responseId}
          onClick={runDryRun}
          data-testid="bundle130-rescan-dry-run-btn"
        >
          <Beaker className="h-4 w-4 mr-1.5" /> Dry-run
        </Button>
        {!confirmApply ? (
          <Button
            disabled={busy || !responseId || !dryRun}
            onClick={() => setConfirmApply(true)}
            className="bg-primary-800 text-white hover:bg-primary-900"
            data-testid="bundle130-rescan-apply-btn"
          >
            <Play className="h-4 w-4 mr-1.5" /> Apply
          </Button>
        ) : (
          <div className="flex items-center gap-2 rounded border border-red-200 bg-red-50 px-2 py-1"
                data-testid="bundle130-rescan-confirm-inline">
            <span className="text-[11px] text-red-800">
              Confirm apply?
            </span>
            <Button size="sm" variant="outline"
                    onClick={() => setConfirmApply(false)}
                    data-testid="bundle130-rescan-confirm-cancel">
              No
            </Button>
            <Button size="sm"
                    onClick={runApply}
                    className="bg-red-600 text-white hover:bg-red-700"
                    data-testid="bundle130-rescan-confirm-yes">
              Yes, apply
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
